import { useCallback, useEffect, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { usePermission } from '../../hooks/usePermission'
import {
  hostedEvents,
  money,
  rebates,
  type HostedEventDetail as EventDetail,
  type RebateConfig,
} from '../../services/hostedEvents'
import { RebateProgramPanel } from './RebateProgramPanel'
import { RebatesTable } from './RebatesTable'
import { VendorsPanel } from './VendorsPanel'
import { dateRange, errorMessage } from './helpers'
import { ErrorBanner, Spinner, Stat, StatusBadge, card, sectionTitle } from './ui'

const TABS = [
  ['overview', 'Overview'],
  ['vendors', 'Vendors'],
  ['rules', 'Rebate rules'],
  ['rebates', 'Rebates'],
] as const
type Tab = (typeof TABS)[number][0]

export function HostedEventDetail() {
  const { marketId = '' } = useParams()
  const [params, setParams] = useSearchParams()
  const canSeeRebates = usePermission('event_rebates', 'read')
  const [event, setEvent] = useState<EventDetail | null>(null)
  const [config, setConfig] = useState<RebateConfig | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [configError, setConfigError] = useState<string | null>(null)
  const [version, setVersion] = useState(0)

  const requested = params.get('tab') as Tab | null
  const tab: Tab =
    requested && TABS.some(([key]) => key === requested) && canSeeRebates ? requested : 'overview'

  const load = useCallback(async () => {
    setError(null)
    setConfigError(null)
    // The overview doesn't need the rebate config, so its failure only
    // affects the rebate tabs.
    const [detail, rebateConfig] = await Promise.allSettled([
      hostedEvents.detail(marketId),
      canSeeRebates ? rebates.config() : Promise.resolve(null),
    ])
    if (detail.status === 'fulfilled') setEvent(detail.value)
    else setError(errorMessage(detail.reason))
    if (rebateConfig.status === 'fulfilled') setConfig(rebateConfig.value)
    else setConfigError(errorMessage(rebateConfig.reason))
  }, [marketId, canSeeRebates])

  useEffect(() => {
    load()
  }, [load])

  const changed = () => {
    setVersion((current) => current + 1)
    load()
  }

  if (error && !event) return <ErrorBanner message={error} onRetry={load} />
  if (!event) return <Spinner />

  const totals = event.totals
  const statuses = Object.entries(event.applications_by_status).sort(([, a], [, b]) => b - a)

  return (
    <div>
      <Link
        to="/hosted-events"
        className="text-sm text-[#7f4f9a] hover:underline dark:text-[#b58fcc]"
      >
        ← Hosted events
      </Link>
      <div className="mb-6 mt-2 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="font-heading text-2xl font-bold text-gray-900 dark:text-white">
              {event.name}
            </h2>
            <StatusBadge status={event.status} />
          </div>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {dateRange(event.starts_at, event.ends_at)}
            {event.venue_name && ` · ${event.venue_name}`}
            {event.city && ` · ${[event.city, event.state_code].filter(Boolean).join(', ')}`}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Organized by {event.organizer_name || 'unknown'}
            {event.organizer_handle && ` (@${event.organizer_handle})`}
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
          Rebate program:
          {event.program ? (
            <StatusBadge status={event.program.status} />
          ) : (
            <span className="text-gray-400">none</span>
          )}
        </div>
      </div>

      {error && <ErrorBanner message={error} onRetry={load} />}

      {canSeeRebates && (
        <div
          className="mb-6 flex gap-1 border-b border-gray-200 dark:border-[#2d2236]"
          role="tablist"
        >
          {TABS.map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              onClick={() => setParams(key === 'overview' ? {} : { tab: key }, { replace: true })}
              className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${
                tab === key
                  ? 'border-[#7f4f9a] text-[#503864] dark:text-white'
                  : 'border-transparent text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              {label}
              {key === 'rebates' && totals.pending_count > 0 && (
                <span className="ml-1.5 rounded-full bg-amber-100 px-1.5 text-xs text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                  {totals.pending_count}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {tab === 'overview' && (
        <div className="space-y-6">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Stat
              label="Paid vendors"
              value={event.paid_vendors}
              hint={event.capacity ? `of ${event.capacity} spots` : undefined}
            />
            <Stat
              label="Checked in"
              value={event.checked_in_vendors}
              hint={`${event.no_show_vendors} no-show`}
            />
            <Stat
              label="Booth fees"
              value={money(event.booth_revenue_cents)}
              hint={
                event.booth_refunded_cents > 0
                  ? `${money(event.booth_refunded_cents)} refunded`
                  : 'None refunded'
              }
            />
            <Stat
              label="Rebates committed"
              value={money(totals.committed_cents)}
              hint={
                totals.remaining_budget_cents === null
                  ? `${totals.pending_count} waiting for review`
                  : `${money(totals.remaining_budget_cents)} of budget left`
              }
            />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div className={`${card} p-5`}>
              <h3 className={sectionTitle}>Applications</h3>
              {statuses.length === 0 ? (
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  No applications yet.
                </p>
              ) : (
                <ul className="mt-3 space-y-2">
                  {statuses.map(([status, count]) => (
                    <li key={status} className="flex items-center justify-between text-sm">
                      <StatusBadge status={status} />
                      <span className="font-medium text-gray-900 dark:text-white">{count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className={`${card} p-5`}>
              <h3 className={sectionTitle}>Details</h3>
              <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
                {(
                  [
                    ['Mode', event.mode],
                    ['Spots', event.spot_kind],
                    ['Vendors pay', event.vendors_pay ? 'Yes' : 'No'],
                    ['Slug', event.slug],
                    ['Address', event.address || 'None'],
                  ] as [string, string][]
                ).map(([label, value]) => (
                  <div key={label}>
                    <dt className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      {label}
                    </dt>
                    <dd className="text-gray-900 dark:text-white">{value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </div>
      )}

      {!config && configError && tab !== 'overview' && (
        <ErrorBanner message={configError} onRetry={load} />
      )}
      {config && tab === 'vendors' && (
        <VendorsPanel marketId={marketId} config={config} onChanged={changed} />
      )}
      {config && tab === 'rules' && (
        <RebateProgramPanel
          key={event.program ? `${event.program.id}-${event.program.revision}` : 'new'}
          marketId={marketId}
          program={event.program}
          config={config}
          vendorsPay={event.vendors_pay}
          onChanged={changed}
        />
      )}
      {config && tab === 'rebates' && (
        <RebatesTable key={version} marketId={marketId} config={config} onChanged={load} />
      )}
    </div>
  )
}
